/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2024-01-26 00:00:00
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import moment from 'moment';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
  DataTable,
  Card,
  SkeletonDisplayText,
  Tooltip,
  Icon,
  Badge,
  Pagination,
  Button,
  ActionList,
  Popover
} from '@shopify/polaris';
import { QuestionMarkMajor } from '@shopify/polaris-icons';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { TickSmallMinor } from '@shopify/polaris-icons';

import NoHistory from 'src/components/views/sync/sync-history/no-history';

import {
  SYNC_LIST,
  NUMBER_SYNC_PER_PAGE,
  SYNC_TRIGGER_TYPE,
  SYNC_TRIGGER_TYPE_LABEL
} from 'src/constants/sync';
import { formatDatetime } from 'src/helpers';

import * as syncActions from 'actions/sync-actions';

type State = {
  selected: number,
  skip: number,
  hasPrevious: boolean,
  hasNext: boolean
};

type Props = {
  emailSupport: string,
  getSyncHistoryLoading: boolean,
  syncHistory: Array<Object>,
  total: number,
  getSyncHistory: Function
};

type ComposedProps = Props & WithI18nProps;

const renderRowsLoadingFilter = count => {
  let rows = [];
  for (var i = 0; i < count; i++) {
    rows.push([
      <SkeletonDisplayText size="small" />,
      <SkeletonDisplayText size="small" />,
      <SkeletonDisplayText size="small" />,
      <SkeletonDisplayText size="small" />,
      <SkeletonDisplayText size="small" />
    ]);
  }

  return rows;
};

class SyncHistory extends Component<ComposedProps, State> {
  state = {
    selected: 0,
    skip: 0,
    hasPrevious: false,
    hasNext: false,
    active: false,
    page: 1,
    total: 0,
    totalPage: 1
  };

  componentDidMount() {
    const { getSyncHistory } = this.props;
    getSyncHistory(0, NUMBER_SYNC_PER_PAGE, '');
  }

  componentWillReceiveProps(nextProps) {
    const { skip } = this.state;
    const { getSyncHistoryLoading: nextGetSyncHistoryLoading, total: nextTotal } = nextProps;
    const { getSyncHistoryLoading } = this.props;

    if (getSyncHistoryLoading && !nextGetSyncHistoryLoading) {
      const hasNext = skip + NUMBER_SYNC_PER_PAGE < nextTotal;
      const hasPrevious = skip > 0;

      let updatedState = {
        hasNext,
        hasPrevious,
        total: nextTotal,
        totalPage: Math.round(nextTotal / NUMBER_SYNC_PER_PAGE),
        page: skip / NUMBER_SYNC_PER_PAGE + 1
      };
      this.setState(updatedState);
    }
  }

  render() {
    const { i18n, getSyncHistoryLoading } = this.props;
    const { selected, hasPrevious, hasNext, active, page, totalPage } = this.state;

    // Render row on table
    let rows;
    if (!getSyncHistoryLoading) {
      rows = this.buildRows();
    } else {
      rows = renderRowsLoadingFilter(10);
    }

    const activator = (
      <Button
        onClick={() => {
          this.setState({ active: !this.state.active });
        }}
        disclosure="select">
        {i18n.translate(`AdminHTML.Sync.SyncHistory.tab${selected + 1}_Content`)}
      </Button>
    );

    return (
      <div className="sync-history">
        <Card
          title={
            <div className="sync-history-header">
              <h2 className="Polaris-Heading">
                {i18n.translate('AdminHTML.Sync.SyncHistory.title')}
              </h2>
              <div className="action">
                <span style={{ color: '#6D7175' }}>
                  {i18n.translate('AdminHTML.Sync.SyncHistory.triggeredBy')}
                </span>
                <Popover
                  active={active}
                  activator={activator}
                  onClose={() => {
                    this.setState({ active: false });
                  }}>
                  <div className="sync-history-trigger-popover">
                    <ActionList
                      sections={[
                        {
                          // title: i18n.translate('AdminHTML.SwatchSettings.SwatchSettings.searchNote'),
                          items: [
                            {
                              id: 'all',
                              content: i18n.translate('AdminHTML.Sync.SyncHistory.tab1_Content'),
                              suffix: selected === 0 && <Icon source={TickSmallMinor} />,
                              active: selected === 0,
                              onAction: () => this.handleTabChange(0)
                            },
                            {
                              id: 'manual-sync',
                              content: i18n.translate('AdminHTML.Sync.SyncHistory.tab2_Content'),
                              suffix: selected === 1 && <Icon source={TickSmallMinor} />,
                              active: selected === 1,
                              onAction: () => this.handleTabChange(1)
                            },
                            {
                              id: 'auto-sync',
                              content: i18n.translate('AdminHTML.Sync.SyncHistory.tab3_Content'),
                              suffix: selected === 2 && <Icon source={TickSmallMinor} />,
                              active: selected === 2,
                              onAction: () => this.handleTabChange(2)
                            },
                            {
                              id: 'scheduled-sync',
                              content: i18n.translate('AdminHTML.Sync.SyncHistory.tab4_Content'),
                              suffix: selected === 3 && <Icon source={TickSmallMinor} />,
                              active: selected === 3,
                              onAction: () => this.handleTabChange(3)
                            }
                          ]
                        }
                      ]}
                    />
                  </div>
                </Popover>
              </div>
            </div>
          }>
          <div className="BC-Sync__Content">
            <DataTable
              verticalAlign="middle"
              columnContentTypes={['text', 'text', 'text']}
              headings={[
                i18n.translate('AdminHTML.Sync.SyncHistory.syncType'),
                i18n.translate('AdminHTML.Sync.SyncHistory.triggeredBy'),
                i18n.translate('AdminHTML.Sync.SyncHistory.startedAt'),
                i18n.translate('AdminHTML.Sync.SyncHistory.duration'),
                i18n.translate('AdminHTML.Sync.SyncHistory.status')
              ]}
              rows={rows}
            />
          </div>
        </Card>
        <div className="sync-history-pagination">
          {(hasPrevious || hasNext) && (
            <div className="pagination">
              <Pagination
                label={`${page}/${totalPage}`}
                hasPrevious={hasPrevious}
                onPrevious={this.getPreviousPage}
                hasNext={hasNext}
                onNext={this.getNextPage}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  // Handle change tab
  handleTabChange = (selectedTabIndex: number) => {
    const { getSyncHistory } = this.props;

    getSyncHistory(0, NUMBER_SYNC_PER_PAGE, SYNC_TRIGGER_TYPE[selectedTabIndex]);
    this.setState({ selected: selectedTabIndex, skip: 0 });
  };

  renderStatus = status => {
    const { i18n } = this.props;
    const statusList = {
      error: (
        <Badge status="critical">
          <span className="status-icon"></span>
          {i18n.translate('AdminHTML.Sync.SyncHistory.status_error')}
        </Badge>
      ),
      done: (
        <Badge status="success">
          <span className="status-icon"></span>
          {i18n.translate('AdminHTML.Sync.SyncHistory.status_completed')}
        </Badge>
      ),
      completed: (
        <Badge status="success">
          <span className="status-icon"></span>
          {i18n.translate('AdminHTML.Sync.SyncHistory.status_completed')}
        </Badge>
      )
    };
    if (!_.isEmpty(statusList[status])) {
      return statusList[status];
    }
    return status;
  };

  buildRows = () => {
    const { i18n, syncHistory } = this.props;
    let rows = [];
    if (!_.isEmpty(syncHistory)) {
      _.forEach(syncHistory, item => {
        const {
          status,
          flowName = '',
          triggeredBy,
          startedAt = 0,
          duration,
          longDurationWarning
        } = item;
        let flowLabel = SYNC_LIST[flowName];
        flowLabel = !_.isEmpty(flowLabel)
          ? flowLabel
          : 'AdminHTML.Sync.Constants.flowManualSync_Title'; // Default is flowManualSync, if flowName is not in SYNC_LIST

        if (item) {
          rows.push([
            flowLabel ? i18n.translate(flowLabel) : flowLabel,
            SYNC_TRIGGER_TYPE_LABEL[triggeredBy]
              ? i18n.translate(SYNC_TRIGGER_TYPE_LABEL[triggeredBy])
              : triggeredBy,
            formatDatetime(startedAt),
            <span>
              {moment.duration(duration, 'seconds').humanize()}
              {longDurationWarning ? (
                <span className="tooltip">
                  <Tooltip
                    content={
                      <span className="tooltipContent tooltip-long-sync">
                        {i18n.translate(`AdminHTML.Sync.SyncHistory.longDurationWarning`)}
                      </span>
                    }
                    preferredPosition="above">
                    <Icon source={QuestionMarkMajor} color="base" />
                  </Tooltip>
                </span>
              ) : (
                ''
              )}
            </span>,
            this.renderStatus(status)
          ]);
        } else {
          rows.push([
            <SkeletonDisplayText size="small" />,
            <SkeletonDisplayText size="small" />,
            <SkeletonDisplayText size="small" />,
            <SkeletonDisplayText size="small" />,
            <SkeletonDisplayText size="small" />
          ]);
        }
      });
    } else {
      rows.push([<NoHistory />]);
    }

    return rows;
  };

  // Go to previous page
  getPreviousPage = () => {
    const { skip, selected } = this.state;
    const previousSkip = skip - NUMBER_SYNC_PER_PAGE;

    // Prevent multiple click that the skip number is less than 0
    if (previousSkip < 0) return;

    // Set skip, limit for next page
    this.setState({ skip: previousSkip });
    this.props.getSyncHistory(previousSkip, NUMBER_SYNC_PER_PAGE, SYNC_TRIGGER_TYPE[selected]);
  };

  // Go to next page
  getNextPage = () => {
    const { skip, selected } = this.state;
    const { total } = this.props;
    const nextSkip = skip + NUMBER_SYNC_PER_PAGE;

    // Prevent multiple click that the skip number is bigger than total
    if (nextSkip >= total) return;

    // Set skip, limit for next page
    this.setState({ skip: nextSkip });
    this.props.getSyncHistory(nextSkip, NUMBER_SYNC_PER_PAGE, SYNC_TRIGGER_TYPE[selected]);
  };
}

const mapStateToProps = state => ({
  getSyncHistoryLoading: _.get(state.sync, 'getSyncHistoryLoading', false),
  syncHistory: _.get(state.sync, 'syncHistory', []),
  total: _.get(state.sync, 'syncHistoryTotal', 0)
});

const mapDispatchToProps = (dispatch, ownProps) => ({
  // Get sync history
  getSyncHistory: (skip: number, limit: number, triggeredBy: string) =>
    dispatch(syncActions.getSyncHistory(skip, limit, triggeredBy, ownProps.i18n))
});

export default connect(mapStateToProps, mapDispatchToProps)(withI18n()(SyncHistory));
